import React, { FC } from 'react'
import { View, Image, StyleSheet, Pressable } from 'react-native'
import { LinearGradient, type LinearGradientProps } from 'expo-linear-gradient'

import Text from '@common/components/Text'
import { createGlobalTooltipDataSet } from '@common/components/GlobalTooltip'
import SkeletonLoader from '@common/components/SkeletonLoader'
import useTheme from '@common/hooks/useTheme'
import spacings from '@common/styles/spacings'
import flexbox from '@common/styles/utils/flexbox'
import GlobeIcon from '@common/assets/svg/GlobeIcon'
import ShieldFilledIcon from '@common/assets/svg/ShieldFilledIcon'
import logo from '@web/assets/images/logo.png'
import logoLight from '@web/assets/images/logoLight.png'
import { THEME_TYPES } from '@common/styles/themeConfig'
import useAppMode from '@web/hooks/useAppMode'
import { SkeletonLoaderProps } from '@common/components/SkeletonLoader/types'
import { ModeEnum, Mode } from '@web/contexts/appModeContext'
import {
  PRIVATE_MODE_TOOLTIP,
  PrivateModeReason
} from '@web/utils/privateModeAvailability'

interface TabItem {
  id: Mode
  label: string
  iconComponent: typeof GlobeIcon
}

interface HeaderProps {
  mode: Mode
  /**
   * Why the Private tab is/isn't usable for the current account. `'available'` enables the tab;
   * any other value disables it and surfaces the matching tooltip from `PRIVATE_MODE_TOOLTIP`.
   */
  privateModeReason?: PrivateModeReason
  onModeChange: (mode: Mode) => void
  /**
   * Called when the user clicks the Private tab while it's in the `failed` state.
   * Should re-attempt Railgun engine initialization.
   */
  onRetryRailgunInit?: () => void
}

const Header: FC<HeaderProps> = ({
  mode,
  privateModeReason = 'available',
  onModeChange,
  onRetryRailgunInit
}) => {
  const { theme, themeType } = useTheme(getStyles)
  const { railgunEngineInitializing, railgunEngineInitializingError } = useAppMode()
  const logoURI = themeType === THEME_TYPES.DARK ? logo : logoLight
  const tabs: TabItem[] = [
    {
      id: 'public',
      label: 'Public',
      iconComponent: GlobeIcon
    },
    {
      id: 'private',
      label: 'Private',
      iconComponent: ShieldFilledIcon
    }
  ]

  const skeletonProps: SkeletonLoaderProps = {
    width: 62,
    height: 16,
    appearance: 'secondaryBackgroundInverted',
    style: localStyles.skeleton
  }

  const gradientMapping: Record<Mode, LinearGradientProps> = {
    public: {
      colors: ['#0A125C', '#063AEB', '#48E4CC'],
      locations: [0.1206, 0.6319, 1.0544],
      start: { x: 0, y: 0 },
      end: { x: 1, y: 1 }
    },
    private: {
      colors: ['#654901', '#AF851D'],
      locations: [0.0184, 1.0083],
      start: { x: 0, y: 0 },
      end: { x: 1, y: 1 }
    }
  }

  return (
    <View
      style={[
        spacings.mtSm,
        spacings.ph,
        flexbox.directionRow,
        flexbox.alignCenter,
        flexbox.justifySpaceBetween,
        { maxWidth: 990, width: '100%', marginHorizontal: 'auto' }
      ]}
    >
      <Image source={{ uri: logoURI }} style={localStyles.logo} />
      <View
        style={[
          localStyles.tabs,
          { backgroundColor: theme.secondaryBackground },
          flexbox.directionRow,
          flexbox.alignCenter,
          flexbox.justifyCenter
        ]}
      >
        {tabs.map((tab) => {
          const isActive = mode === tab.id
          const iconColor = isActive ? theme.white : theme.secondaryText
          const IconComponent = tab.iconComponent
          const isPrivateTab = tab.id === ModeEnum.PRIVATE
          const isPrivateUnavailable = isPrivateTab && privateModeReason !== 'available'
          const isSkeletonVisible =
            isPrivateTab &&
            (railgunEngineInitializing || privateModeReason === 'initializing')
          // `failed` is the only unavailable reason that should accept a click — clicking
          // re-runs `ensureRailgunEngine`. Every other unavailable reason (no-seed,
          // hardware, view-only, creatable, initializing) stays disabled.
          const allowRetry = isPrivateTab && privateModeReason === 'failed'
          const isDisabled =
            isPrivateTab &&
            !allowRetry &&
            (isPrivateUnavailable ||
              railgunEngineInitializing ||
              railgunEngineInitializingError)
          const tooltipReason: Exclude<PrivateModeReason, 'available'> | null =
            isPrivateUnavailable
              ? (privateModeReason as Exclude<PrivateModeReason, 'available'>)
              : null
          // `pressableNativeDisabled` exists so an in-flight init doesn't accept a click that
          // would race the init promise. We *do* allow click while in `failed` so the retry
          // affordance works.
          const pressableNativeDisabled =
            isPrivateTab && !allowRetry && railgunEngineInitializing

          const tabPressable = (
            <Pressable
              disabled={isPrivateTab ? pressableNativeDisabled : false}
              onPress={() => {
                if (allowRetry) {
                  onRetryRailgunInit?.()
                  return
                }
                if (!isDisabled) {
                  onModeChange(tab.id)
                }
              }}
            >
              {isActive ? (
                <LinearGradient
                  {...gradientMapping[tab.id]}
                  style={[
                    localStyles.tab,
                    spacings.pvMi,
                    spacings.phTy,
                    flexbox.directionRow,
                    flexbox.alignCenter,
                    flexbox.justifyCenter,
                    isDisabled && localStyles.disabledTab
                  ]}
                >
                  {isSkeletonVisible ? (
                    <SkeletonLoader {...skeletonProps} />
                  ) : (
                    <>
                      <IconComponent width={16} height={16} color={iconColor} />
                      <Text fontSize={12} weight="medium" color={theme.white}>
                        {tab.label}
                      </Text>
                    </>
                  )}
                </LinearGradient>
              ) : (
                <View
                  style={[
                    localStyles.tab,
                    spacings.pvMi,
                    spacings.phTy,
                    flexbox.directionRow,
                    flexbox.alignCenter,
                    flexbox.justifyCenter,
                    isDisabled && localStyles.disabledTab
                  ]}
                >
                  {isSkeletonVisible ? (
                    <SkeletonLoader {...skeletonProps} />
                  ) : (
                    <>
                      <IconComponent width={16} height={16} color={iconColor} />
                      <Text fontSize={12} weight="medium" color={theme.secondaryText}>
                        {tab.label}
                      </Text>
                    </>
                  )}
                </View>
              )}
            </Pressable>
          )

          if (isPrivateTab) {
            return (
              <View
                key={tab.id}
                dataSet={createGlobalTooltipDataSet({
                  id: 'header-private-unavailable',
                  content: tooltipReason ? PRIVATE_MODE_TOOLTIP[tooltipReason] : '',
                  hidden: !tooltipReason
                })}
              >
                {tabPressable}
              </View>
            )
          }

          return <React.Fragment key={tab.id}>{tabPressable}</React.Fragment>
        })}
      </View>
    </View>
  )
}

const getStyles = () => ({})

const localStyles = StyleSheet.create({
  logo: {
    width: 95,
    height: 32
  },
  tabs: {
    padding: 2,
    borderRadius: 100
  },
  tab: {
    borderRadius: 100,
    gap: 4
  },
  disabledTab: {
    opacity: 0.5
  },
  skeleton: {
    marginVertical: 4
  }
})

export default React.memo(Header)
